import React, { useContext, useState } from "react";
import Form from "../../../ui/Form/Form";
import AddRange from "../../../ui/range/AddRange";
import TabPanel from "../../../ui/tabs/TabPanel";
import UpdateExistingRange from "../../../ui/range/UpdateExistingRange";
import { errorAlert, makeApiRequest, successAlert } from "../../../util";
import AuthContext from "../../../store/authContext";
import InfoAlert from "../../../ui/InfoAlert";
import {
  CREATE_XSSI_CONNECTION_TRANSLATIONS,
  EDIT_XSSI_CONNECTION_TRANSLATIONS,
} from "../../../translations/util";

export type TUnitRangeType = "UNIT" | "IWF";

interface IBaseRange {
  ID: number;
  CreationDate: string;
  ModificationDate: string;
}
interface IIWFUnitRange extends IBaseRange {
  "IWF-UNIT-MIN-ID": number;
  "IWF-UNIT-MAX-ID": number;
}

interface IRadioUnitRanges extends IBaseRange {
  "UNIT-MIN-ID": number;
  "UNIT-MAX-ID": number;
}

export type TUnitRanges = IIWFUnitRange | IRadioUnitRanges;

const UNIT_RANGE_CONFIG = {
  UNIT: {
    urlSegment: "unit",
    minKey: "UNIT-MIN-ID",
    maxKey: "UNIT-MAX-ID",
  },
  IWF: {
    urlSegment: "iwf-unit",
    minKey: "IWF-UNIT-MIN-ID",
    maxKey: "IWF-UNIT-MAX-ID",
  },
} as const;
interface IProps {
  ranges: Array<TUnitRanges>;
  xssiRef: string;
  org: string;
  refetchConnection: () => Promise<void>;
  hasEditPermission: boolean;
  hasDeletePermission: boolean;
  unitType: TUnitRangeType;
}

const getHeadingName = (unitType: string) => {
  if (unitType === "IWF") {
    return EDIT_XSSI_CONNECTION_TRANSLATIONS.range.heading.iwfUnit;
  }
  return EDIT_XSSI_CONNECTION_TRANSLATIONS.range.heading.unit;
};

const UpdateUnitRanges = ({
  ranges,
  xssiRef,
  org,
  refetchConnection,
  hasEditPermission,
  hasDeletePermission,
  unitType = "UNIT",
}: IProps) => {
  // GLOBAL STATE(s)
  const { token } = useContext(AuthContext);
  // LOCAL STATE(s)
  const [addLoading, setAddLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { urlSegment } = UNIT_RANGE_CONFIG[unitType];
  const finalURL = `/api/v1/app-config/xssi-connection-ranges/${xssiRef}/range/${urlSegment}`;

  const addToRange = async (min: number, max: number) => {
    setAddLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: finalURL,
        method: "POST",
        body: JSON.stringify({
          "MIN-ID": +min,
          "MAX-ID": +max,
        }),
        token,
        org,
        emptyResponse: true,
      });
      successAlert(EDIT_XSSI_CONNECTION_TRANSLATIONS.range.successAlertMessage);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      refetchConnection();
      setAddLoading(false);
    }
  };

  const deleteRange = (idx: number) => async () => {
    setDeleteLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `${finalURL}/${idx}`,
        method: "DELETE",
        token,
        org,
        emptyResponse: true,
      });
      successAlert(
        EDIT_XSSI_CONNECTION_TRANSLATIONS.range.deleteSuccessAlertMessage
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      refetchConnection();
      setDeleteLoading(false);
    }
  };

  const updateRange = async (min: number, max: number, id: number) => {
    setEditLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `${finalURL}/${id}`,
        method: "PUT",
        body: JSON.stringify({
          "MIN-ID": +min,
          "MAX-ID": +max,
        }),
        token,
        org,
        emptyResponse: true,
      });
      successAlert(
        EDIT_XSSI_CONNECTION_TRANSLATIONS.range.editSuccessAlertMessage
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      refetchConnection();
      setEditLoading(false);
    }
  };

  const fetchLowerAndUpperBounds = (range: TUnitRanges) => {
    return unitType === "IWF"
      ? {
          min: (range as IIWFUnitRange)["IWF-UNIT-MIN-ID"],
          max: (range as IIWFUnitRange)["IWF-UNIT-MAX-ID"],
        }
      : {
          min: (range as IRadioUnitRanges)["UNIT-MIN-ID"],
          max: (range as IRadioUnitRanges)["UNIT-MAX-ID"],
        };
  };

  return (
    <div className="data-tabcontent">
      <TabPanel>
        <div className="data-form">
          <Form>
            {hasEditPermission ? (
              <AddRange
                loading={addLoading}
                addRangeTitle={
                  unitType === "IWF"
                    ? CREATE_XSSI_CONNECTION_TRANSLATIONS.ranges
                        .createNewIWFUnitText
                    : CREATE_XSSI_CONNECTION_TRANSLATIONS.ranges
                        .createNewUnitRangeText
                }
                labelPrefix={
                  unitType === "IWF"
                    ? EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                        .iwfUnitLabelPrefix
                    : EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                        .unitLabelPrefix
                }
                addToRange={addToRange}
              />
            ) : null}
            <div className="data-card">
              <h3 data-cy={`${unitType}Range`}>
                {getHeadingName(unitType)}{" "}
                {EDIT_XSSI_CONNECTION_TRANSLATIONS.range.ranges}{" "}
              </h3>
              {ranges.length < 1 ? (
                <InfoAlert
                  heading={EDIT_XSSI_CONNECTION_TRANSLATIONS.range.infoAlert.heading(
                    getHeadingName(unitType)
                  )}
                  subText={
                    EDIT_XSSI_CONNECTION_TRANSLATIONS.range.unitRangeInfoAlert
                      .subText
                  }
                />
              ) : null}
              {ranges.map((range) => {
                // eslint-disable-next-line react/no-array-index-key
                const { min, max } = fetchLowerAndUpperBounds(range);
                return (
                  <UpdateExistingRange
                    editloading={editLoading}
                    deleteLoading={deleteLoading}
                    key={range.ID}
                    labelPrefix={
                      unitType === "IWF"
                        ? EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                            .iwfUnitLabelPrefix
                        : EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                            .unitLabelPrefix
                    }
                    range={{
                      id: range.ID,
                      creationDate: range.CreationDate,
                      modificationDate: range.ModificationDate,
                      min: min as number,
                      max: max as number,
                    }}
                    deleteRange={deleteRange(range.ID)}
                    updateRange={updateRange}
                    hasEditPermission={hasEditPermission}
                    hasDeletePermission={hasDeletePermission}
                  />
                );
              })}
            </div>
          </Form>
        </div>
      </TabPanel>
    </div>
  );
};

export default UpdateUnitRanges;
